import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useRef, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export interface DialogProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  title: string;
  /** Mono uppercase subtitle under the title. */
  subtitle?: string;
  /** Screen-reader description when the body is not self-describing. */
  description?: string;
  children: ReactNode;
  /** Right-aligned footer actions: tertiary buttons then one primary. */
  actions?: ReactNode;
  /** Panel width in px. Default 520. */
  width?: number;
  /** Element that opens the dialog (wrapped in Radix Trigger). */
  trigger?: ReactNode;
  /** Hide the corner close button (still closes on Esc). */
  hideClose?: boolean;
  /** Prevent closing by clicking the overlay or pressing Esc (confirmations mid-operation). */
  modalOnly?: boolean;
  className?: string;
}

/** Radix Dialog: hairline --line panel on --panel, no shadow, dark overlay. */
export function Dialog({
  open,
  defaultOpen,
  onOpenChange,
  title,
  subtitle,
  description,
  children,
  actions,
  width = 520,
  trigger,
  hideClose = false,
  modalOnly = false,
  className,
}: DialogProps) {
  // Radix returns focus to its Trigger on close and prevents the FocusScope default, so a
  // controlled dialog opened without a Trigger would drop focus to <body>. Remember whatever had
  // focus when the dialog opened and restore it ourselves in that case (A28 keyboard flow).
  //
  // The opener is captured in the render that flips `open` to true, before the content commits:
  // React applies a child `autoFocus` during commit, so by the time Radix fires onOpenAutoFocus the
  // active element is already the field inside the dialog. onOpenAutoFocus then refreshes the capture
  // when focus still sits outside the content (a menu that closed and returned focus to its trigger)
  // and never records an element inside the dialog.
  const returnFocusTo = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);
  if (open === true && !wasOpen.current) {
    const active = document.activeElement;
    returnFocusTo.current = active instanceof HTMLElement && active !== document.body ? active : null;
  }
  wasOpen.current = open === true;
  const rememberOpener = (event: Event) => {
    const content = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    const active = document.activeElement;
    if (active instanceof HTMLElement && active !== document.body && !content?.contains(active)) {
      returnFocusTo.current = active;
    }
  };
  const restoreOpener = (event: Event) => {
    if (trigger) return;
    const el = returnFocusTo.current;
    returnFocusTo.current = null;
    if (!el || !el.isConnected) return;
    event.preventDefault();
    el.focus({ preventScroll: true });
  };
  return (
    <RadixDialog.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      {trigger ? <RadixDialog.Trigger asChild>{trigger}</RadixDialog.Trigger> : null}
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-[80] bg-black/60" />
        <RadixDialog.Content
          onOpenAutoFocus={rememberOpener}
          onCloseAutoFocus={restoreOpener}
          onEscapeKeyDown={modalOnly ? (e) => e.preventDefault() : undefined}
          onPointerDownOutside={modalOnly ? (e) => e.preventDefault() : undefined}
          onInteractOutside={modalOnly ? (e) => e.preventDefault() : undefined}
          // without a Description element Radix expects an explicit undefined here
          {...(description ? {} : { 'aria-describedby': undefined })}
          style={{ width: `min(${width}px, calc(100vw - 32px))` }}
          className={cn(
            'fixed left-1/2 top-1/2 z-[90] flex max-h-[calc(100vh-32px)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-sm border border-line bg-panel text-ink outline-none',
            className,
          )}
        >
          <header className="flex items-start justify-between gap-3 border-b border-hair px-5 py-3">
            <div className="min-w-0">
              <RadixDialog.Title className="text-nav font-medium tracking-tight text-ink">{title}</RadixDialog.Title>
              {subtitle ? <p className="mt-0.5 font-mono text-2xs uppercase tracking-[0.15em] text-mute">{subtitle}</p> : null}
              {description ? <RadixDialog.Description className="sr-only">{description}</RadixDialog.Description> : null}
            </div>
            {!hideClose ? (
              <RadixDialog.Close
                aria-label="Close"
                className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-sm text-mute transition-colors hover:bg-card hover:text-ink"
              >
                <X size={12} aria-hidden />
              </RadixDialog.Close>
            ) : null}
          </header>
          <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto px-5 py-4 text-nav leading-snug text-ink/85">{children}</div>
          {actions ? <footer className="flex items-center justify-end gap-2 border-t border-hair px-5 py-3">{actions}</footer> : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

export const DialogClose = RadixDialog.Close;
